/*
 * WORK > Proposals (the old Steward's 08-proposals.js): commercial cleaning bids priced by the hour from a walkthrough.
 * The list; the builder in four steps (the business, the walkthrough room by room, the price with three options from
 * the crew rates, and the proposal's words); and Proposal settings. A proposal is a quote: Save makes a draft quote, and
 * sending, the client's signature and making the job happen on the quote's page. The server prices it again on save.
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  PROPOSAL_AREA_WORDS,
  PROPOSAL_AREAS,
  PROPOSAL_DAYS,
  PROPOSAL_FIXTURES,
  PROPOSAL_FREQS,
  PROPOSAL_INDUSTRIES,
  PROPOSAL_INDUSTRY_WORDS,
  PROPOSAL_PHASE_WORDS,
  PROPOSAL_PHASES,
  PROPOSAL_SCOPE_KEYS,
  PROPOSAL_SERVICE_WORDS,
  PROPOSAL_SERVICES,
  PROPOSAL_SETTINGS_DEFAULTS,
  proposalInsurance,
  proposalTermsText,
  QUOTE_STATUS_WORDS,
  QUOTE_STATUSES,
  proposalCalc,
  proposalScopeFor,
  proposalScopeFit,
  proposalScopeKeys,
  proposalScopeWords,
  proposalStockWords,
  proposalVisitsAMonth,
  proposalSplit,
  proposalSqft,
  proposalWalk,
  proposalPartCents,
  proposalPartsOnce,
  proposalPartsTotal,
  proposalPricedParts,
  addDays,
  mondayOf,
  oneLine,
  splitAddress,
  type ProposalCalc,
  type ProposalPart,
  type ProposalIndustry,
  type ProposalOption,
  type ProposalPick,
  type ProposalRow,
  type ProposalService,
  type ProposalSettings,
  type ProposalWalk,
  type QuoteStatus,
  type VisitCard,
} from "@steward/shared";
import { useClients, useSettings, useUpdateSettings, useVisits } from "../api/hooks";
import { saveProposal, useArchiveQuote, useProposal, useProposals, useRestoreQuote, type ProposalIn } from "../api/money";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, Combobox, FormField, InputText } from "../components/fields";
import { dayDate, minutesWords, money, moneyExact, monthName, plural, shortDate, timeRange, todayIn } from "../components/format";
import { useToast } from "../components/overlays";
import { AddressFields, type AddressDraft } from "../components/SubLocations";
import { Banner, Button, Card, Chip, EmptyState, IconButton, LoadError, Page, Skeleton } from "../components/parts";
import { errText } from "../components/work";
import { QuoteTag } from "./Quotes";
import { SettingsButton } from "./settings/Feature";

const NEW = "new";
const DAY_ORDER = [...PROPOSAL_DAYS];
const pct = (n: number) => `${String(Math.round(n))}%`;
const per = (c: Pick<ProposalCalc, "once">) => (c.once ? "one time" : "a month");
/** Dollars typed into a box, as whole cents. */
const toCentsTyped = (v: string) => Math.max(0, Math.round(Number(v.replace(/[$,\s]/g, "")) * 100) || 0);
const dollars = (c: number) => (c ? String(c / 100) : "");

function useProposalSettings(): ProposalSettings {
  const { company, can } = useSession();
  const s = useSettings(company.id).data?.settings;
  return can("settings.read") && s ? s.proposals : PROPOSAL_SETTINGS_DEFAULTS;
}

/** Proposals are for commercial cleaning bids; other trades send quotes. */
function NotCleaning() {
  const navigate = useNavigate();
  return (
    <Page title="Proposals">
      <EmptyState icon="quotes" title="Proposals are for commercial cleaning bids">
        This business sends quotes instead.
        <div>
          <Button type="secondary" onClick={() => void navigate({ to: "/quotes" })}>
            Open Quotes
          </Button>
        </div>
      </EmptyState>
    </Page>
  );
}

// The list ----------------------------------------------------------------------------------------------------------

type Show = QuoteStatus | "all" | "archived";

export function ProposalsPage() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const list = useProposals(company.id);
  const [show, setShow] = useState<Show>("all");
  if (company.trade !== "cleaning") return <NotCleaning />;
  const all = list.data?.proposals ?? [];
  const live = all.filter((p) => !p.archived);
  const rows = all.filter((p) => (show === "archived" ? p.archived : !p.archived && (show === "all" || p.status === show)));
  return (
    <Page
      title="Proposals"
      primaryAction={
        can("quote.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/proposals/new" })}>
            New proposal
          </Button>
        ) : null
      }
      secondaryAction={<SettingsButton to="/proposals/settings">Proposal settings</SettingsButton>}
    >
      <DataTable<ProposalRow>
        title="All proposals"
        rows={rows}
        rowKey={(p) => p.quoteId}
        loading={list.isFetching}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        onRowClick={(p) =>
          void navigate(
            p.status === "draft" && !p.archived && can("quote.update")
              ? { to: "/proposals/$id", params: { id: p.quoteId } }
              : { to: "/quotes/$id", params: { id: p.quoteId } },
          )
        }
        search={(p) => [p.business, p.contactName, p.number].join(" ")}
        tools={
          <div className="chips">
            <Chip pressed={show === "all"} onClick={() => setShow("all")}>
              All
            </Chip>
            {QUOTE_STATUSES.map((s) => (
              <Chip key={s} pressed={show === s} onClick={() => setShow(s)}>
                {QUOTE_STATUS_WORDS[s]} ({live.filter((p) => p.status === s).length})
              </Chip>
            ))}
            <Chip pressed={show === "archived"} onClick={() => setShow("archived")}>
              Archived ({all.length - live.length})
            </Chip>
          </div>
        }
        empty={
          all.length ? (
            <EmptyState icon="quotes" title="No proposals match">
              Try another status.
            </EmptyState>
          ) : (
            <EmptyState icon="quotes" title="No proposals yet">
              Walk the building, put in the rooms, and the Steward prices it by the hour. Send the proposal and they sign it on
              their phone.
            </EmptyState>
          )
        }
        columns={[
          {
            key: "business",
            header: "Business",
            sort: (p) => p.business.toLowerCase(),
            render: (p) => (
              <div className="name-cell">
                <b>{p.business}</b>
                {p.contactName && p.contactName !== p.business ? <span className="muted">{p.contactName}</span> : null}
              </div>
            ),
          },
          { key: "number", header: "Number", sort: (p) => p.number, render: (p) => `#${String(p.number)}` },
          { key: "service", header: "Service", render: (p) => PROPOSAL_SERVICE_WORDS[p.service].replace(" (hourly)", "") },
          { key: "created", header: "Created", sort: (p) => p.createdAt, render: (p) => shortDate(p.createdAt.slice(0, 10)) },
          {
            key: "status",
            header: "Status",
            sort: (p) => QUOTE_STATUSES.indexOf(p.status),
            render: (p) => <QuoteTag status={p.status} />,
          },
          {
            key: "price",
            header: "Price",
            align: "right",
            sort: (p) => p.totalCents,
            render: (p) => (
              <div className="name-cell">
                <span>{money(p.totalCents)}</span>
                {p.monthly ? <span className="muted">a month</span> : null}
              </div>
            ),
          },
        ]}
      />
    </Page>
  );
}

// The builder -------------------------------------------------------------------------------------------------------

interface Draft {
  who: string;
  company: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  /** In parts, like a client's (owner rule): Street 1, Street 2, City, State, ZIP. */
  address: AddressDraft;
  walk: ProposalWalk;
  /** The restroom counts were guessed from the total size, not typed. */
  fxGuess: boolean;
}

function blank(): Draft {
  const walk = proposalWalk.parse({
    rooms: [{ name: "", kind: "office", sqft: 0 }],
    days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
  });
  walk.scope = proposalScopeFor(walk.service, walk.industry);
  return {
    who: "",
    company: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    address: noAddress(),
    walk,
    fxGuess: false,
  };
}

const noAddress = (): AddressDraft => ({ street1: "", street2: "", city: "", state: "", zip: "" });
/** A saved one line address back in its parts to edit. */
function addressOf(line: string | null): AddressDraft {
  const p = splitAddress(line);
  return { street1: p.street1 ?? "", street2: p.street2 ?? "", city: p.city ?? "", state: p.state ?? "", zip: p.zip ?? "" };
}

const STEPS = ["Business", "Walkthrough", "Price", "Proposal"] as const;

export function ProposalBuilder({ id }: { id?: string }) {
  const { company } = useSession();
  const detail = useProposal(id ?? "", Boolean(id));
  if (company.trade !== "cleaning") return <NotCleaning />;
  if (id && detail.error)
    return (
      <Page title="Proposal">
        <LoadError error={detail.error} retry={() => void detail.refetch()} />
      </Page>
    );
  if (id && !detail.data)
    return (
      <Page title="Proposal">
        <Skeleton rows={6} />
      </Page>
    );
  const d = detail.data;
  if (d && d.status !== "draft")
    return (
      <Page title="Proposal">
        <Banner type="notice">This proposal was already sent. Make a new one to change it.</Banner>
      </Page>
    );
  const start: Draft = d
    ? {
        who: d.clientId ?? NEW,
        company: d.clientId ? "" : d.building,
        contactName: d.contactName && d.contactName !== d.building ? d.contactName : "",
        contactPhone: d.contactPhone ?? "",
        contactEmail: d.contactEmail ?? "",
        address: addressOf(d.address),
        walk: { ...d.walk, scope: d.walk.scope ?? proposalScopeFor(d.walk.service, d.walk.industry) },
        fxGuess: false,
      }
    : blank();
  return <Builder key={id ?? "new"} id={id ?? null} start={start} />;
}

function Builder({ id: firstId, start }: { id: string | null; start: Draft }) {
  const { company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const s = useProposalSettings();
  const clients = useClients(company.id, { status: "active", sort: "last_activity" });
  const [f, setF] = useState<Draft>(start);
  const [step, setStep] = useState(0);
  const [id, setId] = useState<string | null>(firstId);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const archive = useArchiveQuote();
  const restore = useRestoreQuote();
  const w = f.walk;
  const calc = proposalCalc(w, s);
  const client = clients.data?.items.find((c) => c.id === f.who);
  const co = client?.companyName?.trim() ?? "";
  const building = client ? co || client.name : f.company.trim() || f.contactName.trim();
  const cons = w.service === "construction";
  // The scope lists follow how often we come: weekly tasks at twice a month are every visit tasks.
  const visitsAMonth = proposalVisitsAMonth(w);
  const scopeKeys = proposalScopeKeys(w);
  const scope = proposalScopeFit(w, w.scope ?? proposalScopeFor(w.service, w.industry));
  const set = (patch: Partial<Draft>) => setF((x) => ({ ...x, ...patch }));
  const walk = (patch: Partial<ProposalWalk>) => setF((x) => ({ ...x, walk: { ...x.walk, ...patch } }));

  const body = (autosave: boolean): ProposalIn => ({
    ...w,
    clientId: f.who && f.who !== NEW ? f.who : null,
    company: f.who === NEW ? f.company.trim() || null : null,
    contactName: f.who === NEW ? f.contactName.trim() || null : null,
    contactPhone: f.who === NEW ? f.contactPhone.trim() || null : null,
    contactEmail: f.who === NEW && f.contactEmail.trim() ? f.contactEmail.trim() : null,
    address: f.who === NEW ? oneLine(f.address) || null : null,
    autosave,
  });

  // Autosave: kept as a draft a moment after the typing stops, once it has a business and a price.
  const idRef = useRef(id);
  idRef.current = id;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const t = setTimeout(() => {
      setNote("Saving...");
      saveProposal(idRef.current, body(true))
        .then((r) => {
          if (r.saved && r.quoteId) {
            setId(r.quoteId);
            setNote("Saved as a draft");
          } else setNote("Saves as a draft once it has a business and a price");
        })
        .catch(() => setNote("Not saved yet"));
    }, 2000);
    return () => clearTimeout(t);
    // body reads f, which is what changes.
  }, [f]);

  const go = (n: number) => {
    if (n > 0 && !building) {
      setStep(0);
      setErr("Pick the business, or add it as a new client.");
      return;
    }
    setErr(null);
    setStep(n);
    window.scrollTo(0, 0);
  };
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await saveProposal(id, body(false));
      toast("Proposal saved. Send it from here when you're ready.");
      void navigate({ to: "/quotes/$id", params: { id: r.quoteId ?? "" } });
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  // Preview (owner, 2026-11-03: "i still cant view the proposal anywhere"): saves the draft as it is now and opens the
  // client's page in a new tab, so the builder keeps its place. The tab opens first so the browser does not block it.
  const [looking, setLooking] = useState(false);
  const previewIt = async () => {
    const tab = window.open("", "_blank");
    setLooking(true);
    setErr(null);
    try {
      const r = await saveProposal(idRef.current, body(true));
      if (!r.saved || !r.quoteId) {
        tab?.close();
        setErr("Add the business and a price first. Then you can preview it.");
        return;
      }
      setId(r.quoteId);
      setNote("Saved as a draft");
      const url = `/preview/quote/${r.quoteId}`;
      if (tab) tab.location.href = url;
      else void navigate({ to: "/preview/quote/$id", params: { id: r.quoteId } });
    } catch (e) {
      tab?.close();
      setErr(errText(e));
    } finally {
      setLooking(false);
    }
  };
  const cancel = () => {
    if (id) toast("Kept as a draft under Proposals.");
    void navigate({ to: "/proposals" });
  };
  // Deleting a draft archives its quote: off the list, and Undo or the Archived tab brings it back.
  const drop = () => {
    if (!id) return;
    const was = id;
    archive.mutate(was, {
      onSuccess: () => {
        toast("Draft deleted", { undo: () => restore.mutate(was) });
        void navigate({ to: "/proposals" });
      },
      onError: (e) => setErr(errText(e)),
    });
  };

  const picked = calc.options[w.pick] ?? calc.options.std;
  // Line items with their own prices set the price, like the server does on save.
  const byParts = proposalPricedParts(w).length > 0;
  const priceCents = byParts ? proposalPartsTotal(w) : picked && calc.hours ? picked.monthlyCents : 0;
  const priceWords = byParts ? (proposalPartsOnce(w) ? "one time" : "a month") : per(calc);
  return (
    <Page
      title={id ? "Edit proposal" : "New proposal"}
      subtitle={note || undefined}
      primaryAction={
        <Button type="secondary" icon="arrowUpRight" onClick={() => void previewIt()} loading={looking}>
          Preview
        </Button>
      }
      moreActions={
        id ? (
          <Button type="tertiary" variation="destructive" onClick={drop} loading={archive.isPending}>
            Delete draft
          </Button>
        ) : null
      }
      secondaryAction={
        <Button type="tertiary" onClick={cancel}>
          Cancel
        </Button>
      }
    >
      <div className="prop-steps" role="tablist" aria-label="Steps">
        {STEPS.map((label, i) => (
          <button
            key={label}
            type="button"
            role="tab"
            aria-selected={i === step}
            className={`prop-step${i === step ? " on" : ""}${i < step ? " done" : ""}`}
            onClick={() => go(i)}
          >
            <span>{i + 1}</span>
            {label}
          </button>
        ))}
      </div>
      <div className="split">
        <div className="stack">
          {err ? <Banner type="error">{err}</Banner> : null}
          {step === 0 ? (
            <Card title="Business">
              <div className="stack">
                <Combobox
                  label="Who it is for"
                  value={f.who}
                  onChange={(v) => set({ who: v })}
                  options={(clients.data?.items ?? []).map((c) => ({
                    value: c.id,
                    label: c.companyName ? `${c.companyName} (${c.name})` : c.name,
                  }))}
                  last={{ value: NEW, label: "New client", onPick: (typed) => set({ company: typed }) }}
                />
                {f.who === NEW ? (
                  // One tight block like Add client on a quote, in a form so the browser offers to fill it.
                  <form
                    className="client-form prop-part"
                    autoComplete="on"
                    onSubmit={(e) => e.preventDefault()}
                    aria-label="New client"
                  >
                    <div className="span-6">
                      <InputText
                        label="Business name"
                        name="organization"
                        autoComplete="organization"
                        value={f.company}
                        onChange={(e) => set({ company: e.target.value })}
                      />
                    </div>
                    <div className="span-2 wide-phone">
                      <InputText
                        label="Who to talk to"
                        name="name"
                        autoComplete="name"
                        value={f.contactName}
                        onChange={(e) => set({ contactName: e.target.value })}
                      />
                    </div>
                    <div className="span-2 wide-phone">
                      <InputText
                        label="Mobile"
                        type="tel"
                        name="tel"
                        autoComplete="tel"
                        value={f.contactPhone}
                        onChange={(e) => set({ contactPhone: e.target.value })}
                      />
                    </div>
                    <div className="span-2 wide-phone">
                      <InputText
                        label="Email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={f.contactEmail}
                        onChange={(e) => set({ contactEmail: e.target.value })}
                      />
                    </div>
                    <div className="span-6">
                      <AddressFields value={f.address} onChange={(address) => set({ address })} />
                    </div>
                  </form>
                ) : null}
                <div className="grid-2">
                  <FormField label="Industry" htmlFor="pp-industry">
                    <select
                      id="pp-industry"
                      className="select"
                      value={w.industry}
                      onChange={(e) => {
                        const industry = e.target.value as ProposalIndustry;
                        const split = w.totalSqft ? proposalSplit(industry, w.totalSqft) : null;
                        walk({
                          industry,
                          ...(split ? { rooms: split.rooms } : {}),
                          scope: proposalScopeFor(w.service, industry),
                        });
                      }}
                    >
                      {PROPOSAL_INDUSTRIES.map((k) => (
                        <option key={k} value={k}>
                          {PROPOSAL_INDUSTRY_WORDS[k]}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <Choice<ProposalService>
                    label="Service"
                    value={w.service}
                    onChange={(service) => walk({ service, scope: proposalScopeFor(service, w.industry) })}
                    options={PROPOSAL_SERVICES.map((k) => ({ value: k, label: PROPOSAL_SERVICE_WORDS[k] }))}
                  />
                </div>
              </div>
            </Card>
          ) : null}
          {step === 1 ? <Walkthrough f={f} setF={setF} /> : null}
          {step === 2 ? <PriceStep w={w} calc={calc} s={s} walk={walk} /> : null}
          {step === 3 ? (
            <Card
              title="Proposal"
              action={
                <Button type="tertiary" size="small" onClick={() => walk({ scope: proposalScopeFor(w.service, w.industry) })}>
                  Reset to the {cons ? "construction" : PROPOSAL_INDUSTRY_WORDS[w.industry].toLowerCase()} list
                </Button>
              }
            >
              <div className="stack">
                <label className="field wide">
                  <span className="label">About their building</span>
                  <textarea
                    rows={3}
                    maxLength={2000}
                    placeholder="Like 12-person office, busy front lobby, alarm code at the side door"
                    value={w.about}
                    onChange={(e) => walk({ about: e.target.value })}
                  />
                  <span className="description">What you saw and what they told you. This shows you listened.</span>
                </label>
                <b>Scope of work</b>
                {scopeKeys.length < PROPOSAL_SCOPE_KEYS.length ? (
                  <span className="muted small">
                    {visitsAMonth === 0
                      ? "One visit, so every task is done on that visit."
                      : `You come ${PROPOSAL_FREQS.find((x) => x.key !== "once" && Math.abs(x.perMonth - visitsAMonth) < 0.01)?.words ?? "less than weekly"}, so the ${scopeKeys.includes("monthly") ? "weekly tasks are" : "weekly and monthly tasks are"} done every visit.`}
                  </span>
                ) : null}
                {scopeKeys.map((k) => {
                  const items = scope[k].map((x) => proposalStockWords(x, w.supplies === "us"));
                  return (
                    <details key={k} className="prop-scope" open={k === "daily"}>
                      <summary>
                        <span>{proposalScopeWords(w.service)[k]}</span>
                        <span className="muted small">
                          {items.length} task{items.length === 1 ? "" : "s"}
                        </span>
                      </summary>
                      <textarea
                        rows={Math.max(3, Math.min(10, items.length + 1))}
                        aria-label={`${proposalScopeWords(w.service)[k]}, one task per line`}
                        value={items.join("\n")}
                        onChange={(e) =>
                          walk({
                            scope: {
                              ...scope,
                              [k]: e.target.value.split("\n").slice(0, 40),
                            },
                          })
                        }
                        onBlur={(e) =>
                          walk({
                            scope: {
                              ...scope,
                              [k]: e.target.value
                                .split("\n")
                                .map((x) => x.trim())
                                .filter(Boolean)
                                .slice(0, 40),
                            },
                          })
                        }
                      />
                    </details>
                  );
                })}
              </div>
            </Card>
          ) : null}
          <div className="prop-foot">
            <span>
              {priceCents ? (
                <>
                  <b>{money(priceCents)}</b> {priceWords}
                </>
              ) : (
                <span className="muted">No price yet</span>
              )}
            </span>
            <span className="row">
              {step > 0 ? (
                <Button type="secondary" onClick={() => go(step - 1)}>
                  Back
                </Button>
              ) : null}
              {step < STEPS.length - 1 ? (
                <Button onClick={() => go(step + 1)}>Next</Button>
              ) : (
                <Button loading={busy} onClick={() => void save()}>
                  Save proposal
                </Button>
              )}
            </span>
          </div>
        </div>
        <div className="stack">
          <Card title="At a glance">
            <dl className="details">
              <div>
                <dt>Business</dt>
                <dd>{building || <span className="muted">Not picked</span>}</dd>
              </div>
              <div>
                <dt>Service</dt>
                <dd>{PROPOSAL_SERVICE_WORDS[w.service]}</dd>
              </div>
              <div>
                <dt>Size</dt>
                <dd>{proposalSqft(w.rooms).toLocaleString("en-US")} sq ft</dd>
              </div>
              <div>
                <dt>Time</dt>
                <dd>
                  {calc.hours
                    ? `About ${String(calc.hours)} hours${cons ? " in all" : ` a visit, ${calc.words}`}`
                    : byParts
                      ? "Set by line items"
                      : "Add rooms"}
                </dd>
              </div>
              <div>
                <dt>Price</dt>
                <dd>{priceCents ? `${moneyExact(priceCents)} ${priceWords}` : "No price yet"}</dd>
              </div>
            </dl>
          </Card>
          {cons ? null : <OpenDays days={w.days} />}
        </div>
      </div>
    </Page>
  );
}

/** "2026-11-01" moved n months. */
function shiftMonth(first: string, n: number): string {
  const [y = 0, m = 1] = first.split("-").map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${String(Math.floor(t / 12))}-${String((t % 12) + 1).padStart(2, "0")}-01`;
}

/** Minutes from "09:30" to "13:00", or 0 when either is missing. */
const minutesOf = (start: string | null, end: string | null) => {
  if (!start || !end) return 0;
  const m = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  return Math.max(0, m(end) - m(start));
};

/** "6h", "4.5h". */
const hoursShort = (mins: number) => `${String(Math.round(mins / 30) / 2)}h`;

/**
 * The schedule beside the builder (owner, 2026-11-03): each day shows its visits and the hours booked, the lightest
 * weekdays are named, and picking a day lists who is booked when and by which crew, so the days offered fit the crews.
 * Days ticked on the walkthrough are outlined.
 */
function OpenDays({ days }: { days: readonly string[] }) {
  const { company } = useSession();
  const today = todayIn(company.timezone);
  const [month, setMonth] = useState(`${today.slice(0, 8)}01`);
  const [picked, setPicked] = useState(today);
  const from = mondayOf(month);
  const to = addDays(mondayOf(addDays(shiftMonth(month, 1), -1)), 6);
  const visits = useVisits(company.id, { from, to });
  const busy = new Map<string, VisitCard[]>();
  for (const v of visits.data?.visits ?? []) {
    if (v.status === "cancelled" || v.status === "skipped") continue;
    busy.set(v.date, [...(busy.get(v.date) ?? []), v]);
  }
  const minsOn = (list: readonly VisitCard[]) => list.reduce((n, v) => n + minutesOf(v.startTime, v.endTime), 0);
  // Hours set the shading; a month with no times falls back to the visit count.
  const load = (list: readonly VisitCard[]) => minsOn(list) || list.length;
  const most = Math.max(1, ...[...busy.values()].map(load));
  const cells: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) cells.push(d);
  // The lightest weekdays this month, by visits a day, so the owner sees which days to offer.
  const inMonth = cells.filter((d) => d.slice(0, 7) === month.slice(0, 7));
  const byWeekday = DAY_ORDER.map((name, i) => {
    const list = inMonth.filter((d) => cells.indexOf(d) % 7 === i);
    const total = list.reduce((n, d) => n + (busy.get(d)?.length ?? 0), 0);
    return { name, avg: list.length ? total / list.length : 0 };
  });
  const lightest = [...byWeekday].sort((a, b) => a.avg - b.avg).slice(0, 3);
  const dayList = [...(busy.get(picked) ?? [])].sort((a, b) => (a.startTime ?? "99").localeCompare(b.startTime ?? "99"));
  const crews = new Set(dayList.flatMap((v) => v.crew.map((c) => c.id))).size;
  const dayMins = minsOn(dayList);
  return (
    <Card
      title="Schedule"
      action={
        <span className="row">
          <IconButton icon="arrowLeft" label="Month before" onClick={() => setMonth(shiftMonth(month, -1))} />
          <IconButton icon="arrowRight" label="Next month" onClick={() => setMonth(shiftMonth(month, 1))} />
        </span>
      }
    >
      <div className="stack">
        <b>{monthName(month)}</b>
        {visits.error ? <span className="muted small">Could not load the schedule.</span> : null}
        <div className="prop-cal" role="grid" aria-label={`Visits in ${monthName(month)}`}>
          {DAY_ORDER.map((d) => (
            <span key={d} className={`prop-cal-head${days.includes(d) ? " on" : ""}`}>
              {d.slice(0, 2)}
            </span>
          ))}
          {cells.map((d, i) => {
            const list = busy.get(d) ?? [];
            const mins = minsOn(list);
            const out = d.slice(0, 7) !== month.slice(0, 7);
            const level = list.length ? Math.min(4, Math.ceil((load(list) / most) * 4)) : 0;
            const label = `${dayDate(d)}, ${list.length ? plural(list.length, "visit") : "open"}${mins ? `, ${minutesWords(mins)} booked` : ""}`;
            return (
              <button
                key={d}
                type="button"
                role="gridcell"
                aria-label={label}
                aria-selected={d === picked}
                title={label}
                onClick={() => setPicked(d)}
                className={[
                  "prop-cal-day",
                  `lvl${String(level)}`,
                  out ? "out" : "",
                  d < today ? "past" : "",
                  d === today ? "today" : "",
                  d === picked ? "sel" : "",
                  days.includes(DAY_ORDER[i % 7] ?? "") ? "pick" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span>{Number(d.slice(8))}</span>
                <small>{list.length ? `${String(list.length)} visit${list.length === 1 ? "" : "s"}` : "Open"}</small>
                <small className="muted">{mins ? hoursShort(mins) : ""}</small>
              </button>
            );
          })}
        </div>
        <span className="muted small">
          Each day shows visits and hours already booked. Darker days are busier. Pick a day to see who is on it.
        </span>
        {visits.data ? (
          <span className="small">
            <b>Lightest days this month</b>{" "}
            {lightest
              .map((x) => `${x.name} (${x.avg ? `${String(Math.round(x.avg * 10) / 10)} a day` : "none booked"})`)
              .join(", ")}
          </span>
        ) : null}
        <div className="prop-cal-list">
          <b>
            {dayDate(picked)}
            <span className="muted">
              {" "}
              {dayList.length
                ? [
                    plural(dayList.length, "visit"),
                    dayMins ? `${minutesWords(dayMins)} booked` : "",
                    crews ? plural(crews, "person", "people") : "",
                  ]
                    .filter(Boolean)
                    .join(", ")
                : "Nothing booked yet"}
            </span>
          </b>
          {dayList.map((v) => (
            <div key={v.ref} className="prop-cal-row">
              <span className="muted">{timeRange(v.startTime, v.endTime)}</span>
              <span>
                {v.clientName}
                {v.crew.length ? <span className="muted"> with {v.crew.map((c) => c.name.split(" ")[0]).join(", ")}</span> : null}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/** The walkthrough: the total size splits into rooms like a typical building, then each room can change. */
function Walkthrough({ f, setF }: { f: Draft; setF: (fn: (x: Draft) => Draft) => void }) {
  const w = f.walk;
  const cons = w.service === "construction";
  const walk = (patch: Partial<ProposalWalk>, more: Partial<Draft> = {}) =>
    setF((x) => ({ ...x, ...more, walk: { ...x.walk, ...patch } }));
  const room = (i: number, patch: Partial<ProposalWalk["rooms"][number]>) =>
    walk({ rooms: w.rooms.map((r, n) => (n === i ? { ...r, ...patch } : r)) });
  const total = w.totalSqft ?? 0;
  const sq = proposalSqft(w.rooms);
  const weekly = (k: string) => /^w\d$/.test(k);
  return (
    <Card title="Walkthrough">
      <div className="stack">
        <InputText
          label="Total size"
          description={`Type the building's square feet and the rooms below fill in like a typical ${PROPOSAL_INDUSTRY_WORDS[w.industry].toLowerCase()}. Change any row after.`}
          inputMode="numeric"
          suffix="sq ft"
          value={w.totalSqft ? String(w.totalSqft) : ""}
          onChange={(e) => {
            const n = Math.max(0, Math.round(Number(e.target.value.replace(/[,\s]/g, "")) || 0));
            const split = proposalSplit(w.industry, n);
            const fx = w.fixtures;
            const typed = (fx.toilet || fx.urinal || fx.sink) && !f.fxGuess;
            if (!split) {
              walk({ totalSqft: n || null });
              return;
            }
            walk(
              { totalSqft: n, rooms: split.rooms, ...(cons || typed ? {} : { fixtures: split.fixtures }) },
              cons || typed ? {} : { fxGuess: true },
            );
          }}
        />
        <b>Room by room</b>
        <div className="prop-rooms">
          <span className="muted small">Room or area</span>
          <span className="muted small">Kind</span>
          <span className="muted small">% of total</span>
          <span className="muted small">Sq ft</span>
          <span className="muted small">How many</span>
          <span />
          {w.rooms.map((r, i) => (
            <div key={i} className="prop-room">
              <div className="input">
                <input
                  aria-label="Room name"
                  placeholder="Like Front offices"
                  value={r.name}
                  onChange={(e) => room(i, { name: e.target.value.slice(0, 60) })}
                />
              </div>
              <select
                className="select"
                aria-label="Kind of room"
                value={r.kind}
                onChange={(e) => room(i, { kind: e.target.value as (typeof PROPOSAL_AREAS)[number] })}
              >
                {PROPOSAL_AREAS.map((k) => (
                  <option key={k} value={k}>
                    {PROPOSAL_AREA_WORDS[k]}
                  </option>
                ))}
              </select>
              <div className="input">
                <input
                  aria-label="Percent of the building"
                  inputMode="numeric"
                  placeholder="%"
                  disabled={!total}
                  value={total && r.sqft ? String(Math.round(((r.sqft * r.count) / total) * 100)) : ""}
                  onChange={(e) => room(i, { sqft: Math.round((total * (Number(e.target.value) || 0)) / 100 / r.count) })}
                />
              </div>
              <div className="input">
                <input
                  aria-label="Square feet"
                  inputMode="numeric"
                  placeholder="Sq ft"
                  value={r.sqft ? String(r.sqft) : ""}
                  onChange={(e) => room(i, { sqft: Math.min(500_000, Math.max(0, Math.round(Number(e.target.value) || 0))) })}
                />
              </div>
              <div className="input">
                <input
                  aria-label="How many like it"
                  inputMode="numeric"
                  value={String(r.count)}
                  onChange={(e) => room(i, { count: Math.min(200, Math.max(1, Math.round(Number(e.target.value) || 1))) })}
                />
              </div>
              <Button
                type="tertiary"
                size="small"
                aria-label="Remove this room"
                onClick={() => walk({ rooms: w.rooms.filter((_, n) => n !== i) })}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        {sq ? (
          <p className="muted small">
            Rooms add up to {sq.toLocaleString("en-US")} sq ft
            {total ? `, ${pct((sq / total) * 100)} of ${total.toLocaleString("en-US")}` : ""}.
          </p>
        ) : null}
        <div>
          <Button
            type="secondary"
            size="small"
            icon="plus"
            disabled={w.rooms.length >= 80}
            onClick={() => walk({ rooms: [...w.rooms, { name: "", kind: w.rooms.at(-1)?.kind ?? "office", sqft: 0, count: 1 }] })}
          >
            Add a room
          </Button>
        </div>
        {cons ? (
          <>
            <b>Phases</b>
            <div className="chips">
              {PROPOSAL_PHASES.map((k) => (
                <Chip
                  key={k}
                  pressed={w.phases.includes(k)}
                  onClick={() =>
                    walk({
                      phases: w.phases.includes(k)
                        ? w.phases.filter((x) => x !== k)
                        : PROPOSAL_PHASES.filter((x) => x === k || w.phases.includes(x)),
                    })
                  }
                >
                  {PROPOSAL_PHASE_WORDS[k]}
                </Chip>
              ))}
            </div>
            <InputText
              label="When"
              placeholder="Like the week of the 20th, before the walkthrough with the owner"
              value={w.time}
              onChange={(e) => walk({ time: e.target.value.slice(0, 60) })}
            />
          </>
        ) : (
          <>
            <b>Restrooms, all together</b>
            <div className="grid-3">
              {PROPOSAL_FIXTURES.map((k) => (
                <InputText
                  key={k}
                  label={{ toilet: "Toilets", urinal: "Urinals", sink: "Sinks" }[k]}
                  inputMode="numeric"
                  value={String(w.fixtures[k])}
                  onChange={(e) =>
                    walk(
                      { fixtures: { ...w.fixtures, [k]: Math.min(500, Math.max(0, Math.round(Number(e.target.value) || 0))) } },
                      { fxGuess: false },
                    )
                  }
                />
              ))}
            </div>
            <FormField label="How often" htmlFor="pp-freq">
              <select
                id="pp-freq"
                className="select"
                value={w.freq}
                onChange={(e) => {
                  const freq = e.target.value;
                  // A new "3 days per week" ticks 3 days; one visit keeps none.
                  const days = weekly(freq) ? DAY_ORDER.slice(0, Number(freq.slice(1))) : freq === "once" ? [] : w.days;
                  walk({ freq, days });
                }}
              >
                {PROPOSAL_FREQS.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.label}
                  </option>
                ))}
              </select>
            </FormField>
            {w.freq !== "once" ? (
              <>
                <b>Which days</b>
                <div className="chips">
                  {DAY_ORDER.map((d) => (
                    <Chip
                      key={d}
                      pressed={w.days.includes(d)}
                      onClick={() => {
                        const days = DAY_ORDER.filter((x) => (x === d ? !w.days.includes(d) : w.days.includes(x)));
                        // Ticking days changes the weekly choice to match.
                        walk({ days, ...(weekly(w.freq) && days.length ? { freq: `w${String(days.length)}` } : {}) });
                      }}
                    >
                      {d}
                    </Chip>
                  ))}
                </div>
              </>
            ) : null}
            <InputText
              label="Time"
              placeholder="Like after 6 PM"
              value={w.time}
              onChange={(e) => walk({ time: e.target.value.slice(0, 60) })}
            />
          </>
        )}
      </div>
    </Card>
  );
}

const OPTION_NAMES: Record<Exclude<ProposalPick, "custom">, string> = { low: "Lower", std: "Standard", high: "Higher" };

function Split({ o, keep }: { o: ProposalOption; keep: number }) {
  return (
    <span className="prop-split">
      <span>
        Crew {money(o.crewCents)}, {moneyExact(o.crewHourCents)} an hour
      </span>
      <span>Supplies {money(o.suppliesCents)}</span>
      <b>
        You keep {money(o.keepCents)} ({pct(keep)})
      </b>
    </span>
  );
}

/** Three prices from the crew rates and your own price. */
function PriceGuide({
  w,
  calc,
  s,
  walk,
}: {
  w: ProposalWalk;
  calc: ProposalCalc;
  s: ProposalSettings;
  walk: (patch: Partial<ProposalWalk>) => void;
}) {
  const [custom, setCustom] = useState(dollars(w.customCents));
  if (calc.noRate)
    return (
      <Card title="Price">
        <Banner type="notice">Maid service uses your hourly rate. Set it in Proposal settings, under Pricing.</Banner>
      </Card>
    );
  if (!calc.hours)
    return (
      <Card title="Price">
        <p className="muted">
          {calc.service === "construction"
            ? "Add rooms on the walkthrough to see the price."
            : "Add rooms or restroom fixtures on the walkthrough to see a price from the time, or price line items below."}
        </p>
      </Card>
    );
  const sum =
    calc.service === "construction"
      ? `About ${String(calc.hours)} hours in all. ${PROPOSAL_PHASES.filter((k) => calc.phases?.[k] !== undefined)
          .map((k) => `${PROPOSAL_PHASE_WORDS[k]} ${String(calc.phases?.[k] ?? 0)}`)
          .join(", ")}.`
      : `About ${String(calc.hours)} hour${calc.hours === 1 ? "" : "s"} a visit, ${calc.words}.${
          calc.service === "maid" && calc.options.std ? ` ${money(calc.options.std.perVisitCents)} a visit.` : ""
        }`;
  const o = calc.options.custom;
  return (
    <Card title="Price">
      <div className="stack">
        <p>{sum}</p>
        <div className="prop-options" role="radiogroup" aria-label="Pick a price">
          {(["low", "std", "high"] as const)
            .filter((k) => calc.options[k])
            .map((k) => {
              const opt = calc.options[k]!;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={w.pick === k}
                  className={`prop-option${w.pick === k ? " on" : ""}`}
                  onClick={() => walk({ pick: k })}
                >
                  <span className="muted">{calc.service === "maid" ? "Your hourly rate" : OPTION_NAMES[k]}</span>
                  <b className="prop-price">
                    {money(opt.monthlyCents)} {per(calc)}
                  </b>
                  <span className="muted small">
                    {opt.atMin
                      ? `The ${money(s.minVisitCents)} minimum a visit`
                      : `${moneyExact(opt.rateCents)} per cleaning hour`}
                  </span>
                  <Split o={opt} keep={calc.keepPercent} />
                </button>
              );
            })}
          <div className={`prop-option${w.pick === "custom" ? " on" : ""}`}>
            <label className="check-row">
              <input type="radio" checked={w.pick === "custom"} onChange={() => walk({ pick: "custom" })} />
              Your price
            </label>
            <InputText
              label={`Price ${per(calc)}`}
              prefix="$"
              inputMode="decimal"
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                walk({ customCents: toCentsTyped(e.target.value), pick: "custom" });
              }}
            />
            {o ? (
              <>
                <span className="muted small">{moneyExact(o.rateCents)} per cleaning hour</span>
                <Split o={o} keep={calc.keepPercent} />
                {o.crewHourCents < s.crewCents.low ? (
                  <Banner type="warning">
                    At this price the crew makes about {moneyExact(o.crewHourCents)} an hour, under your lower rate of{" "}
                    {moneyExact(s.crewCents.low)}.
                  </Banner>
                ) : null}
              </>
            ) : (
              <span className="muted small">Type a price to see the hourly rate and the split.</span>
            )}
          </div>
        </div>
        {calc.marginOk ? null : (
          <Banner type="warning">
            Your margin is {pct(calc.keepPercent)}, under your {pct(s.marginGoal)} goal. Lower the supplies or the crew share in
            Proposal settings.
          </Banner>
        )}
      </div>
    </Card>
  );
}

/** The price step: the price from the walkthrough, line items priced on their own, and the restroom supplies. */
function PriceStep(p: {
  w: ProposalWalk;
  calc: ProposalCalc;
  s: ProposalSettings;
  walk: (patch: Partial<ProposalWalk>) => void;
}) {
  const { w, walk } = p;
  if (w.service === "construction") return <PriceGuide {...p} />;
  return (
    <>
      {proposalPricedParts(w).length ? (
        <Banner type="notice">Your line items set the price. The prices from the walkthrough are only a guide.</Banner>
      ) : null}
      {proposalPricedParts(w).length && !p.calc.hours ? null : <PriceGuide {...p} />}
      <LineItems w={w} walk={walk} />
      <Card title="Supplies">
        <div className="stack">
          <Choice<"client" | "us">
            label="Restroom paper, hand soap and trash liners"
            value={w.supplies}
            onChange={(supplies) => walk({ supplies })}
            options={[
              { value: "client", label: "The client supplies them" },
              { value: "us", label: "We supply them" },
            ]}
          />
          {w.supplies === "us" ? (
            <InputText
              label="Monthly price for supplies"
              description="A line of its own on the proposal. Leave it at 0 to include them in your price."
              prefix="$"
              inputMode="decimal"
              placeholder="0 for included"
              defaultValue={dollars(w.suppliesCents)}
              onChange={(e) => walk({ suppliesCents: toCentsTyped(e.target.value) })}
            />
          ) : null}
        </div>
      </Card>
    </>
  );
}

const blankPart = (): ProposalPart => ({ name: "", freq: "twice", visitCents: 0, optional: false, included: [] });
const listOf = (v: string) =>
  v
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 30);

/**
 * Line items priced on their own: a part of the building, how often, the price a visit, optional or not, and what is
 * included (listed under the line on the proposal). Any with a name and a price set the proposal's price.
 */
function LineItems({ w, walk }: { w: ProposalWalk; walk: (patch: Partial<ProposalWalk>) => void }) {
  const parts = w.parts;
  const put = (i: number, patch: Partial<ProposalPart>) =>
    walk({ parts: parts.map((x, n) => (n === i ? { ...x, ...patch } : x)) });
  const total = proposalPartsTotal(w);
  const extra = proposalPricedParts(w)
    .filter((x) => x.optional)
    .reduce((t, x) => t + proposalPartCents(x), 0);
  return (
    <Card title="Line items">
      <div className="stack">
        <p className="muted small">
          Price parts of the building on their own, each with its own schedule. Optional ones show as add-ons the client can tick.
        </p>
        {parts.map((x, i) => {
          const cents = proposalPartCents(x);
          return (
            <div key={`${String(i)}-${String(parts.length)}`} className="prop-part stack">
              <div className="grid-3">
                <InputText
                  label="Part of the building"
                  placeholder="Like the hall and 2 restrooms"
                  value={x.name}
                  onChange={(e) => put(i, { name: e.target.value.slice(0, 80) })}
                />
                <FormField label="How often" htmlFor={`pp-part-freq-${String(i)}`}>
                  <select
                    id={`pp-part-freq-${String(i)}`}
                    className="select"
                    value={x.freq}
                    onChange={(e) => put(i, { freq: e.target.value })}
                  >
                    {PROPOSAL_FREQS.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <InputText
                  label="Price a visit"
                  prefix="$"
                  inputMode="decimal"
                  defaultValue={dollars(x.visitCents)}
                  onChange={(e) => put(i, { visitCents: toCentsTyped(e.target.value) })}
                />
              </div>
              <label className="field wide">
                <span className="label">What's included</span>
                <textarea
                  rows={Math.max(3, Math.min(10, x.included.length + 1))}
                  placeholder={"One per line, like\nSweep and mop the floors\nClean and stock the restrooms"}
                  defaultValue={x.included.join("\n")}
                  onBlur={(e) => put(i, { included: listOf(e.target.value) })}
                />
                <span className="description">Shows under this line on the proposal.</span>
              </label>
              <div className="row">
                <label className="check-row">
                  <input type="checkbox" checked={x.optional} onChange={(e) => put(i, { optional: e.target.checked })} />
                  Optional, the client can add it
                </label>
                <span className="muted small">
                  {cents ? `${moneyExact(cents)} ${x.freq === "once" ? "one time" : "a month"}` : "No price yet"}
                </span>
                <Button
                  type="tertiary"
                  size="small"
                  variation="destructive"
                  onClick={() => walk({ parts: parts.filter((_, n) => n !== i) })}
                >
                  Remove
                </Button>
              </div>
            </div>
          );
        })}
        <span className="row">
          <Button
            type="secondary"
            size="small"
            icon="plus"
            disabled={parts.length >= 20}
            onClick={() => walk({ parts: [...parts, blankPart()] })}
          >
            Add a line item
          </Button>
          {total || extra ? (
            <span>
              <b>{moneyExact(total)}</b> {proposalPartsOnce(w) ? "one time" : "a month"}
              {extra ? <span className="muted"> plus up to {moneyExact(extra)} in add-ons</span> : null}
            </span>
          ) : null}
        </span>
      </div>
    </Card>
  );
}

// Proposal settings -------------------------------------------------------------------------------------------------

const lines = (v: string) =>
  v
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

/** How proposals are priced and what they say. These apply to every proposal from now on. */
export function ProposalSettingsPage() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const settings = useSettings(company.id);
  const update = useUpdateSettings();
  const s = settings.data?.settings.proposals;
  if (company.trade !== "cleaning") return <NotCleaning />;
  if (!can("settings.update"))
    return (
      <Page title="Proposal settings">
        <Banner type="notice">Only people who can change settings can change these.</Banner>
      </Page>
    );
  if (!s)
    return <Page title="Proposal settings">{settings.error ? <LoadError error={settings.error} /> : <Skeleton rows={8} />}</Page>;
  return (
    <SettingsForm
      s={s}
      busy={update.isPending}
      onSave={(patch) =>
        update.mutate(
          { proposals: patch },
          {
            onSuccess: () => {
              toast("Proposal settings saved.");
              void navigate({ to: "/proposals" });
            },
            onError: (e) => toast(errText(e), { kind: "error" }),
          },
        )
      }
    />
  );
}

function SettingsForm({ s, busy, onSave }: { s: ProposalSettings; busy: boolean; onSave: (p: ProposalSettings) => void }) {
  const navigate = useNavigate();
  const [v, setV] = useState(() => ({
    minVisit: dollars(s.minVisitCents),
    share: String(s.sharePercent),
    supply: String(s.supplyPercent),
    goal: String(s.marginGoal),
    buffer: String(s.bufferPercent),
    maidHour: dollars(s.maidHourCents),
    maidMin: dollars(s.maidMinCents),
    low: dollars(s.crewCents.low),
    std: dollars(s.crewCents.std),
    high: dollars(s.crewCents.high),
    ...Object.fromEntries(PROPOSAL_AREAS.map((k) => [`area_${k}`, String(s.areaRates[k])])),
    ...Object.fromEntries(PROPOSAL_FIXTURES.map((k) => [`fx_${k}`, String(s.fixtureMinutes[k])])),
    ...Object.fromEntries(PROPOSAL_PHASES.map((k) => [`con_${k}`, String(s.construction[k])])),
    intro: s.intro,
    signName: s.signName,
    signTitle: s.signTitle,
    rating: s.rating,
    reviews: s.reviews.join("\n"),
    bring: s.bring.join("\n"),
    insurance: proposalInsurance(s.insurance),
    highlights: s.highlights.join("\n"),
    quality: s.quality,
    terms: proposalTermsText(s.terms),
  }));
  const val = (k: string) => (v as Record<string, string>)[k] ?? "";
  const set = (k: string) => (e: { target: { value: string } }) => setV((x) => ({ ...x, [k]: e.target.value }));
  const n = (k: string, lo: number, hi: number, d: number) => {
    const x = Number(val(k));
    return Number.isFinite(x) && val(k).trim() !== "" ? Math.min(hi, Math.max(lo, x)) : d;
  };
  const c = (k: string, d: number) => (val(k).trim() === "" ? d : toCentsTyped(val(k)));
  const num = (k: string, label: string, unit: string) => (
    <InputText label={label} inputMode="decimal" suffix={unit} value={val(k)} onChange={set(k)} />
  );
  const text = (k: string, label: string, rows: number, description?: string) => (
    <label className="field wide">
      <span className="label">{label}</span>
      <textarea rows={rows} value={val(k)} onChange={set(k)} />
      {description ? <span className="description">{description}</span> : null}
    </label>
  );
  const save = () =>
    onSave({
      minVisitCents: c("minVisit", s.minVisitCents),
      sharePercent: n("share", 10, 90, s.sharePercent),
      supplyPercent: n("supply", 0, 50, s.supplyPercent),
      marginGoal: n("goal", 0, 90, s.marginGoal),
      bufferPercent: n("buffer", 0, 100, s.bufferPercent),
      maidHourCents: c("maidHour", 0),
      maidMinCents: c("maidMin", 0),
      crewCents: {
        low: Math.max(100, c("low", s.crewCents.low)),
        std: Math.max(100, c("std", s.crewCents.std)),
        high: Math.max(100, c("high", s.crewCents.high)),
      },
      areaRates: Object.fromEntries(
        PROPOSAL_AREAS.map((k) => [k, Math.round(n(`area_${k}`, 100, 20_000, s.areaRates[k]))]),
      ) as ProposalSettings["areaRates"],
      fixtureMinutes: Object.fromEntries(
        PROPOSAL_FIXTURES.map((k) => [k, n(`fx_${k}`, 0, 60, s.fixtureMinutes[k])]),
      ) as ProposalSettings["fixtureMinutes"],
      construction: Object.fromEntries(
        PROPOSAL_PHASES.map((k) => [k, Math.round(n(`con_${k}`, 20, 5000, s.construction[k]))]),
      ) as ProposalSettings["construction"],
      intro: val("intro").slice(0, 3000),
      signName: val("signName").trim().slice(0, 60),
      signTitle: val("signTitle").trim().slice(0, 60),
      rating: val("rating").trim().slice(0, 80),
      reviews: lines(val("reviews"))
        .map((x) => x.slice(0, 600))
        .slice(0, 6),
      bring: lines(val("bring"))
        .map((x) => x.slice(0, 200))
        .slice(0, 12),
      insurance: val("insurance").slice(0, 3000),
      highlights: lines(val("highlights"))
        .map((x) => x.slice(0, 200))
        .slice(0, 12),
      quality: val("quality").slice(0, 3000),
      terms: val("terms").slice(0, 3000),
    });
  return (
    <Page
      title="Proposal settings"
      subtitle="How proposals are priced and what they say. These apply to every new proposal."
      primaryAction={
        <Button loading={busy} onClick={save}>
          Save
        </Button>
      }
      secondaryAction={
        <Button type="tertiary" onClick={() => void navigate({ to: "/proposals" })}>
          Cancel
        </Button>
      }
    >
      <Card title="Pricing">
        <p className="muted">
          A visit is priced from the hours it takes and what the crew makes an hour, never below the minimum.
        </p>
        <div className="grid-3">
          {num("minVisit", "Minimum a visit", "dollars")}
          {num("share", "Crew's share of the price", "%")}
          {num("supply", "Cleaning supplies", "% of the price")}
          {num("goal", "Your margin goal", "%")}
          {num("buffer", "Extra time for setup", "% of the hours")}
          {num("maidHour", "Maid service rate", "dollars an hour")}
          {num("maidMin", "Maid service minimum", "dollars a visit")}
        </div>
      </Card>
      <Card title="Crew pay an hour">
        <p className="muted">Each proposal shows three prices, from what the crew makes an hour at their share.</p>
        <div className="grid-3">
          {num("low", "Lower price", "dollars an hour")}
          {num("std", "Standard price", "dollars an hour")}
          {num("high", "Higher price", "dollars an hour")}
        </div>
      </Card>
      <Card title="Cleaning speeds">
        <p className="muted">
          Square feet one person cleans in an hour, for each kind of room. These are starting points. Time your crew on a few
          buildings and put in their real numbers.
        </p>
        <div className="grid-3">
          {PROPOSAL_AREAS.map((k) => (
            <div key={k}>{num(`area_${k}`, PROPOSAL_AREA_WORDS[k], "sq ft an hour")}</div>
          ))}
        </div>
      </Card>
      <Card title="Construction cleanup">
        <p className="muted">Square feet an hour for each phase.</p>
        <div className="grid-3">
          {PROPOSAL_PHASES.map((k) => (
            <div key={k}>{num(`con_${k}`, PROPOSAL_PHASE_WORDS[k], "sq ft an hour")}</div>
          ))}
        </div>
      </Card>
      <Card title="Restroom fixtures">
        <p className="muted">Minutes to clean each one, added to the visit.</p>
        <div className="grid-3">
          {num("fx_toilet", "Toilet", "minutes")}
          {num("fx_urinal", "Urinal", "minutes")}
          {num("fx_sink", "Sink", "minutes")}
        </div>
      </Card>
      <Card title="The letter">
        <div className="stack">
          {text("intro", "Letter", 6, "The note at the top of every proposal, from you. The client's first name goes before it.")}
          <div className="grid-2">
            <InputText label="Signed by" placeholder="Like Halfi Rivera" value={val("signName")} onChange={set("signName")} />
            <InputText label="Title" value={val("signTitle")} onChange={set("signTitle")} />
          </div>
        </div>
      </Card>
      <Card title="What clients say">
        <div className="stack">
          <InputText
            label="Your rating"
            description="Only the number on your Google profile today, like 4.8 stars from 108 Google reviews. Leave it empty to leave it off."
            maxLength={80}
            value={val("rating")}
            onChange={set("rating")}
          />
          {text(
            "reviews",
            "Reviews",
            5,
            "One per line. What they said, then a bar and their name, like Always on time | Karen W. Up to 6.",
          )}
        </div>
      </Card>
      <Card title="What every proposal says">
        <div className="stack">
          {text("bring", "What we bring", 4, "One per line.")}
          {text("insurance", "Insurance", 2)}
          {text("highlights", "Why us", 6, "One per line, up to 12.")}
          {text("quality", "How you keep it right", 3)}
          {text("terms", "Terms", 5, "One per line.")}
        </div>
      </Card>
    </Page>
  );
}
